import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { Button } from './ui/button';
import { useConsent } from '../consent/ConsentContext';

export function ConsentBanner() {
  const { t } = useTranslation();
  const { hasDecision, acceptAll, rejectAll, openModal } = useConsent();

  // The banner is baked into the prerendered (SSG) landing HTML — prerender has no
  // localStorage, so hasDecision=false there and the banner is always present. A
  // visitor who has ALREADY consented computes hasDecision=true on the client and would
  // render null on the first pass: a hydration mismatch against the prerendered banner.
  // On iOS WebKit that mismatch breaks hydration for the whole root — no event handlers
  // attach, so every button goes dead and the static banner stays stuck on screen
  // (exact repro: accept once, then reload). Desktop Blink silently recovers; WebKit
  // does not. Fix: keep the first client render identical to the prerender (banner
  // shown) and only apply the real decision AFTER hydration, so hydration never
  // mismatches. The buttons carry cursor-pointer for the separate iOS click quirk.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);

  if (hydrated && hasDecision) return null;

  const btnClass = 'h-10 flex-1 cursor-pointer touch-manipulation sm:h-7 sm:flex-none';

  return (
    <div
      role="dialog"
      aria-label={t('cookies.banner.ariaLabel')}
      aria-live="polite"
      className="mf-consent-in fixed bottom-0 inset-x-0 z-[60] border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-lg"
    >
      <div className="max-w-5xl mx-auto p-4 sm:p-5 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-[calc(1.25rem+env(safe-area-inset-bottom))] flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
        <p className="flex-1 min-w-0 text-sm text-zinc-800 dark:text-zinc-100">
          {t('cookies.banner.message')}{' '}
          <Link
            href="/privacy"
            className="underline underline-offset-2 font-medium text-zinc-900 dark:text-white hover:text-zinc-600 dark:hover:text-zinc-300"
          >
            {t('cookies.banner.privacyLink')}
          </Link>
        </p>
        <div className="flex items-center gap-2 sm:shrink-0">
          <Button variant="ghost" size="sm" className={btnClass} onClick={openModal}>
            {t('cookies.banner.customize')}
          </Button>
          <Button variant="outline" size="sm" className={btnClass} onClick={rejectAll}>
            {t('cookies.banner.rejectAll')}
          </Button>
          <Button variant="default" size="sm" className={btnClass} onClick={acceptAll}>
            {t('cookies.banner.acceptAll')}
          </Button>
        </div>
      </div>
    </div>
  );
}
